/**
 * Scoring for the drum challenges.
 *
 * Self-contained by design: the drum feature owns its judging rules so it can
 * be tuned — or removed — without touching the piano game. The windows and the
 * grade curve deliberately match the values used elsewhere in the app so that
 * an "A" means the same thing to a player whichever game they are playing.
 */

export type DrumHitRating = 'PERFECT' | 'GOOD' | 'MISS';

export interface DrumHit {
  rating: DrumHitRating;
  points: number;
  /** 1 for a perfect hit, 0.5 for a good one, 0 for a miss. */
  accuracy: number;
}

/** Within this many ms of the target, a hit is PERFECT. */
export const PERFECT_WINDOW_MS = 50;
/** Within this many ms, a hit is GOOD. */
export const GOOD_WINDOW_MS = 100;
/**
 * The furthest a keypress can land from a step and still be counted against
 * it. Presses outside every step's window are ignored rather than punished,
 * and a step still unjudged this far past its time is auto-missed.
 */
export const MAX_HIT_WINDOW_MS = 200;

/** Judge one keypress against the time the step should have been played. */
export function judgeHit(targetMs: number, hitMs: number): DrumHit {
  const offset = Math.abs(targetMs - hitMs);

  if (offset <= PERFECT_WINDOW_MS) {
    return { rating: 'PERFECT', points: 100, accuracy: 1 };
  }
  if (offset <= GOOD_WINDOW_MS) {
    return { rating: 'GOOD', points: 50, accuracy: 0.5 };
  }
  return { rating: 'MISS', points: 0, accuracy: 0 };
}

/** Overall accuracy across a run, as a percentage to two decimals. */
export function overallAccuracy(
  perfect: number,
  good: number,
  miss: number,
): number {
  const total = perfect + good + miss;
  if (total === 0) return 0;
  return Number((((perfect + good * 0.5) / total) * 100).toFixed(2));
}

/** Letter grade for a run, from an accuracy percentage (0–100). */
export function accuracyToGrade(accuracy: number): string {
  if (accuracy >= 100) return 'S';
  if (accuracy >= 95) return 'A+';
  if (accuracy >= 90) return 'A';
  if (accuracy >= 80) return 'B';
  if (accuracy >= 70) return 'C';
  if (accuracy >= 50) return 'D';
  return 'F';
}

/** Every grade the server will accept, so the client can validate before sending. */
export const GRADES = ['S', 'A+', 'A', 'B', 'C', 'D', 'F'] as const;

/** Points multiplier earned by a running combo. */
export function comboMultiplier(combo: number): number {
  if (combo >= 100) return 4;
  if (combo >= 50) return 3;
  if (combo >= 20) return 2;
  return 1;
}
